/**
 * Helix preset library — the names the Inspector picker offers (DEC-151).
 *
 * Source: OPBridge `GET /helix/presets`, which reads them off the pedal over
 * Line 6's proprietary USB session. MIDI cannot carry preset names, so there
 * is no other route; see DEC-150 for why, and for the stall this read causes.
 *
 * ## Why this caches to disk and the Stream Deck catalogue does not
 *
 * `streamDeckActions.ts` is the model for this file and deliberately does NOT
 * persist — refetching it is one cheap local call. Refetching THIS costs ~10s
 * of USB work, and before OPBridge began closing the pedal's session after every
 * read (b0496ae6 / 32d4d485) it left the pedal ignoring MIDI Program Changes
 * until power-cycled (DEC-150). The stall notice below now fires only when that
 * close did not take.
 *
 * So a fetch is something the operator should pay for roughly once, and the
 * result must survive a page reload, a browser restart and a bridge going
 * away. The library is written to localStorage on every successful read and
 * loaded synchronously at module init, which is why a tile can still show
 * "Essex A15" with the bridge offline and the pedal unplugged.
 *
 * ## TWO operations, and only one of them is dangerous
 *
 * OPBridge split these deliberately (S175), and the split is the whole
 * safety story:
 *
 *   - `GET /helix/presets` — serves a CACHE. Never touches USB. Cheap and
 *     safe, so it IS auto-loaded on bridge connect (`installHelixAutoLoad`).
 *   - `POST /helix/probe` — the ~8s USB read. Stalls the pedal's MIDI
 *     Program Changes until the operator replugs the USB or opens+quits HX
 *     Edit. Fires ONLY from an explicit operator press.
 *
 * OPButtons's original ask said "probe on connect and cache". OPBridge
 * declined that half and was right to: "connect" also fires when a cable is
 * reseated, which can happen mid-gig, and auto-probing there would stall the
 * pedal at the worst possible moment.
 *
 * The first version of this file conflated the two — it assumed the GET did
 * the USB read, so it refused to fetch anything automatically. That made
 * "Read from pedal" a no-op against a never-probed bridge, which is exactly
 * what the operator saw.
 *
 * ## Names may be absent; that is a state, not an error
 *
 * `setlists` is optional in the contract — OPBridge may be able to read
 * preset names but not setlist names. Callers get "Setlist 3" in that case.
 * A preset with no name falls back to its slot notation. Never render blank.
 */

import { createSignal, createEffect, untrack } from 'solid-js';
import { primaryHttpClient } from '../opbridge/store.ts';
import { OpbridgeError, type HelixPresetsResponse, type HelixPreset } from '../opbridge/httpClient.ts';

/**
 * Whether OPBridge currently sees the pedal on USB - `null` means unknown
 * (not yet checked, or the last check failed). Backed by `GET /helix/status`,
 * which is enumeration-only and opens no USB session, so it's safe to call
 * freely (OPBridge S175: "use it to decide whether to offer the button at
 * all"). Advisory only - a stale/failed check leaves the button offered
 * rather than blocking it, since the probe's own error handling already
 * covers an absent pedal cleanly.
 */
const [devicePresentSig, setDevicePresent] = createSignal<boolean | null>(null);
export const helixDevicePresent = devicePresentSig;

/** Refresh {@link helixDevicePresent}. Never throws. */
export function refreshHelixDeviceStatus(): void {
  const client = primaryHttpClient();
  if (!client) { setDevicePresent(null); return; }
  void client.helixDeviceStatus()
    .then((res) => setDevicePresent(res.device_present ?? null))
    .catch(() => { /* advisory only - leave prior state */ });
}

export type HelixLibraryStatus =
  /** Nothing attempted this session. `library` may still hold a past read. */
  | 'idle'
  /** Cheap cached GET in flight. */
  | 'loading'
  /** The ~8s USB probe is running. Distinct from `loading` because the UI
   *  must say something completely different — this one is costing the
   *  operator their pedal. */
  | 'probing'
  /** Library loaded and non-empty. */
  | 'ready'
  /** OPBridge is reachable and the pedal is VISIBLE, but nobody has run the
   *  probe yet (`status: "never_probed"`). The fix is one button press, so
   *  this must never be reported as "is your Helix plugged in?". */
  | 'never-probed'
  /** No connected primary OPBridge to ask. */
  | 'no-bridge'
  /** OPBridge has no `/helix/*` routes (404), or cannot see the pedal. */
  | 'unavailable'
  /** Anything else — auth, network, malformed. `error` carries detail. */
  | 'error';

export interface HelixLibrary {
  device?: string | undefined;
  /** Index -> name, only for setlists OPBridge could name. */
  setlistNames: Record<number, string>;
  /** `${setlist}:${pc}` -> preset name. */
  presetNames: Record<string, string>;
  /** Wall-clock ms of the read that produced this. */
  fetchedAt: number;
}

const STORAGE_KEY = 'opbuttons.helix.library.v1';

/** Operator-typed setlist names, stored SEPARATELY from the library.
 *
 *  Separate on purpose: the library is replaced wholesale by every probe,
 *  and folding these into it would silently wipe the operator's typing every
 *  time they re-read the pedal. They are also worth keeping when the library
 *  is cleared entirely — eight strings the operator typed are more expensive
 *  to reproduce than a cache that regenerates in 8 seconds. */
const SETLIST_OVERRIDE_KEY = 'opbuttons.helix.setlistNames.v1';

const EMPTY: HelixLibrary = { setlistNames: {}, presetNames: {}, fetchedAt: 0 };

/** Key for the preset map. Exported so the picker and the item face agree —
 *  a mismatched key here would silently show no names at all. */
export function presetKey(setlist: number, pc: number): string {
  return `${setlist}:${pc}`;
}

function load(): HelixLibrary {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<HelixLibrary>;
    // Defensive: a hand-edited or half-written entry must not take the
    // picker down. Anything unexpected degrades to "no library".
    if (!parsed || typeof parsed !== 'object') return EMPTY;
    return {
      device: typeof parsed.device === 'string' ? parsed.device : undefined,
      setlistNames: (parsed.setlistNames && typeof parsed.setlistNames === 'object')
        ? parsed.setlistNames as Record<number, string> : {},
      presetNames: (parsed.presetNames && typeof parsed.presetNames === 'object')
        ? parsed.presetNames as Record<string, string> : {},
      fetchedAt: typeof parsed.fetchedAt === 'number' ? parsed.fetchedAt : 0,
    };
  } catch {
    return EMPTY;
  }
}

const [librarySig, setLibrary] = createSignal<HelixLibrary>(load());
const [statusSig, setStatus] = createSignal<HelixLibraryStatus>('idle');
const [errorSig, setError] = createSignal<string>('');

export const helixLibrary = librarySig;
export const helixStatus = statusSig;
export const helixError = errorSig;

/** True when we hold names for anything — from this session or a past one.
 *  The picker uses this rather than `status === 'ready'`, so a cached
 *  library still drives the UI on a cold boot with no bridge. */
export const helixHasLibrary = (): boolean =>
  Object.keys(librarySig().presetNames).length > 0;

/** Name for a preset slot, or undefined when we have none. */
export function helixPresetName(setlist: number, pc: number): string | undefined {
  return librarySig().presetNames[presetKey(setlist, pc)];
}

/**
 * The operator's real setlist names, for reference only:
 *
 *   0 FACTORY 1        1 FACTORY 2        2 ORTOPILOT - 66   3 ORTOPILOT - grt
 *   4 TEST GTR PRESET  5 others           6 BASS TESTPRESETS 7 TEMPLATES
 *
 * DELIBERATELY NOT SEEDED into the override map. They were given as the
 * KNOWN-PLAINTEXT for cracking the protocol — hardcoding them would answer a
 * different question than the one asked, and would quietly remove the pressure
 * to find the real source. They live in a comment so a byte search has
 * something to match against; nothing reads them.
 */
function loadOverrides(): Record<number, string> {
  try {
    const raw = localStorage.getItem(SETLIST_OVERRIDE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return (parsed && typeof parsed === 'object') ? parsed as Record<number, string> : {};
  } catch {
    return {};
  }
}

const [overridesSig, setOverrides] = createSignal<Record<number, string>>(loadOverrides());
export const helixSetlistOverrides = overridesSig;

/** Type a setlist's name by hand. Empty string clears it back to whatever
 *  the device (or the placeholder) says. */
export function setHelixSetlistOverride(setlist: number, name: string): void {
  const next = { ...overridesSig() };
  const clean = name.trim();
  if (clean) next[setlist] = clean; else delete next[setlist];
  setOverrides(next);
  try { localStorage.setItem(SETLIST_OVERRIDE_KEY, JSON.stringify(next)); } catch { /* memory only */ }
}

/**
 * Name for a setlist, ALWAYS a readable string. Resolution order:
 *
 *   1. The DEVICE's own name, when OPBridge can read it. Definitionally
 *      correct — it is what is printed on the unit right now — so it wins,
 *      and a typed override silently becomes redundant rather than fighting
 *      it. That is the promise made when the override was added: if the
 *      automatic path ever lands, it takes over on its own.
 *   2. The operator's typed override.
 *   3. "Setlist N".
 */
export function helixSetlistName(setlist: number): string {
  return librarySig().setlistNames[setlist]
    ?? overridesSig()[setlist]
    ?? `Setlist ${setlist + 1}`;
}

/** Whether the label is a real name (from the device OR typed) rather than
 *  the placeholder. The picker dims placeholders so "Setlist 3" is never
 *  mistaken for a name that came from somewhere. */
export function helixSetlistNamed(setlist: number): boolean {
  return typeof librarySig().setlistNames[setlist] === 'string'
    || typeof overridesSig()[setlist] === 'string';
}

/** Where a setlist's label came from — the picker shows this so the operator
 *  can tell a name they typed from one the pedal reported. */
export function helixSetlistNameSource(setlist: number): 'device' | 'typed' | 'placeholder' {
  if (typeof librarySig().setlistNames[setlist] === 'string') return 'device';
  if (typeof overridesSig()[setlist] === 'string') return 'typed';
  return 'placeholder';
}

/** Setlists worth offering: any we have a name for, plus any that contain
 *  presets. Sorted. Falls back to all 8 when the library is empty, so the
 *  picker is still navigable before a read. */
export function helixSetlists(): number[] {
  const lib = librarySig();
  const seen = new Set<number>();
  for (const k of Object.keys(lib.setlistNames)) seen.add(Number(k));
  // Typed names count too — the operator must be able to name a setlist
  // whose presets have not been read, or naming is only possible after a
  // probe, which defeats the point of having a manual path at all.
  for (const k of Object.keys(overridesSig())) seen.add(Number(k));
  for (const k of Object.keys(lib.presetNames)) {
    const sl = Number(k.split(':')[0]);
    if (Number.isFinite(sl)) seen.add(sl);
  }
  if (seen.size === 0) return [0, 1, 2, 3, 4, 5, 6, 7];
  return [...seen].sort((a, b) => a - b);
}

/** Presets in one setlist as `{ pc, name }`, sorted by pc. */
export function helixPresetsIn(setlist: number): { pc: number; name: string }[] {
  const lib = librarySig();
  const out: { pc: number; name: string }[] = [];
  for (const [k, name] of Object.entries(lib.presetNames)) {
    const [sl, pc] = k.split(':').map(Number);
    if (sl === setlist && Number.isFinite(pc)) out.push({ pc: pc as number, name });
  }
  return out.sort((a, b) => a.pc - b.pc);
}

function normalize(res: HelixPresetsResponse): HelixLibrary {
  const setlistNames: Record<number, string> = {};
  for (const sl of res.setlists ?? []) {
    if (typeof sl?.index !== 'number') continue;
    const n = sl.name?.trim();
    if (n) setlistNames[sl.index] = n;
  }
  const presetNames: Record<string, string> = {};
  for (const p of res.presets ?? []) {
    if (!isUsable(p)) continue;
    const n = p.name?.trim();
    if (!n) continue;
    // Belt and braces on `PC = index % 128`. OPBridge is supposed to send
    // `pc` pre-normalized, but odd setlists report raw indices 128-255 and
    // a single un-normalized value here would map a name onto a slot that
    // cannot be addressed — a control labelled "Surf" firing something
    // else, which is the one failure mode this arc must not ship.
    // Use `pc`, NEVER `index`. OPBridge's S175 correction: `index` is global
    // across the device (setlist 7 starts at 896), not a per-setlist 0-255
    // wrap as this project first recorded. `pc` is already the 0-127 value
    // derived from the u16 index. The `& 0x7f` is a last-ditch guard so a
    // malformed payload can never map a name onto an unaddressable slot —
    // a control labelled "Surf" firing something else is the one failure
    // this arc must not ship.
    presetNames[presetKey(p.setlist, p.pc & 0x7f)] = n;
  }
  return {
    device: typeof res.device === 'string' ? res.device : undefined,
    setlistNames,
    presetNames,
    fetchedAt: Date.now(),
  };
}

function isUsable(p: HelixPreset | undefined): p is HelixPreset {
  return !!p && typeof p.setlist === 'number' && typeof p.pc === 'number';
}

/** Set after a probe that left the pedal stalled, carrying OPBridge's own
 *  recovery wording. Surfacing this is not optional: a probe reports
 *  `midi_program_changes_stalled: true` and, unsurfaced, the operator finds
 *  out when a preset button stops working mid-set. */
const [stallSig, setStall] = createSignal<string>('');
export const helixStallNotice = stallSig;

/** What OPBridge said, from the `{"ok":false,"error":"..."}` body a failed
 *  read answers with; '' when the body is not that. */
function opbridgeSaid(err: OpbridgeError): string {
  const at = err.message.indexOf('{');
  if (at < 0) return '';
  try {
    const body: unknown = JSON.parse(err.message.slice(at));
    const e = body && typeof body === 'object' ? (body as { error?: unknown }).error : undefined;
    return typeof e === 'string' ? e : '';
  } catch { return ''; }
}

/** HX Edit holds the pedal's USB while it is open, so OPBridge cannot read
 *  it; OPBridge names it in its error. Said as one fix, the way OPBridge's own
 *  screen says it (operator, 2026-10-10: "needs to be super easy"). */
export const HELIX_HX_EDIT_OPEN =
  'HX Edit is open, and it holds the Helix\'s USB. Quit HX Edit, then press "Read from pedal" again. Nothing on the pedal was changed.';

/** A failed `Read from pedal`: OPBridge's reason, in its own words. */
function applyProbeError(err: unknown): void {
  if (!(err instanceof OpbridgeError) || err.httpStatus === 404 || err.httpStatus === 503) { applyError(err); return; }
  const said = (opbridgeSaid(err) || err.message).trim().replace(/\.$/, '');
  setStatus('error');
  if (/hx edit/i.test(said)) { setError(HELIX_HX_EDIT_OPEN); return; }
  // On Windows an open HX Edit can show up as "no device" rather than "busy".
  const hint = /no supported line 6 device/i.test(said) ? ' If HX Edit is open, quit it and try again.' : '';
  setError(`Couldn't read the pedal: ${said}. Nothing on the pedal was changed.${hint}`);
}

function applyError(err: unknown): void {
  if (err instanceof OpbridgeError) {
    const notThere = err.httpStatus === 404 || err.httpStatus === 503;
    setStatus(notThere ? 'unavailable' : 'error');
    setError(err.httpStatus === 404
      ? 'This OPBridge has no /helix routes yet.'
      : err.httpStatus === 503
        ? 'OPBridge cannot reach the Helix. Is it plugged in, and HX Edit closed?'
        : `${err.kind}: ${err.message}`);
  } else {
    setStatus('error');
    setError(String(err));
  }
}

/** Apply a `GET /helix/presets` body. Returns how many names it carried. */
function apply(res: HelixPresetsResponse): number {
  const lib = normalize(res);
  const count = Object.keys(lib.presetNames).length;
  if (count === 0) {
    // An empty list is NOT an error and NOT an empty pedal. OPBridge answers
    // 200 + `never_probed` before anyone has read the device, and says
    // whether the pedal is present — so report the state that is actually
    // true and name the one-press fix.
    if (res.status === 'never_probed' || res.device_present) {
      setStatus('never-probed');
      setError(res.hint ?? 'No names read from the pedal yet.');
    } else {
      setStatus('unavailable');
      setError('OPBridge reports no Helix. Is it plugged in, and HX Edit closed?');
    }
    return 0;
  }
  setLibrary(lib);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(lib)); } catch { /* quota — memory only */ }
  setStatus('ready');
  setError('');
  return count;
}

let loadInFlight: Promise<void> | null = null;

/**
 * CHEAP. `GET /helix/presets` — serves OPBridge's cache, never touches USB.
 * Safe to call on connect and on picker-open. Never throws.
 *
 * A failure deliberately leaves any persisted library in place: losing names
 * because the bridge blinked would be a pointless regression, and the only
 * way to get them back costs the pedal.
 */
export function loadHelixLibrary(): Promise<void> {
  if (loadInFlight) return loadInFlight;
  // A read in progress fetches the names itself when it ends; a load now
  // would overwrite its 'probing' state.
  if (probeInFlight) return probeInFlight;
  loadInFlight = (async () => {
    const client = primaryHttpClient();
    if (!client) { setStatus('no-bridge'); setError(''); return; }
    setStatus('loading');
    try {
      apply(await client.helixPresets());
    } catch (err) {
      applyError(err);
    }
  })().finally(() => { loadInFlight = null; });
  return loadInFlight;
}

let probeInFlight: Promise<void> | null = null;

/**
 * EXPENSIVE AND DISRUPTIVE. `POST /helix/probe` — ~8s of USB, and it leaves
 * the pedal ignoring MIDI Program Changes until the operator replugs the USB
 * or opens+quits HX Edit.
 *
 * ONLY ever call this from an explicit operator press. Never on connect: a
 * "connect" also fires when a cable is reseated, which can happen mid-gig.
 *
 * Probes, then re-reads the cache, because the probe response carries counts
 * rather than the library itself.
 */
export function probeHelixDevice(): Promise<void> {
  if (probeInFlight) return probeInFlight;
  probeInFlight = (async () => {
    const client = primaryHttpClient();
    if (!client) { setStatus('no-bridge'); setError(''); return; }
    setStatus('probing');
    setStall('');
    try {
      const probe = await client.helixProbe();

      // ─── Clear OPBridge's half automatically ───────────────────────
      // A probe leaves TWO separate casualties, and only one of them needs
      // the operator:
      //
      //   1. OPBridge's own MIDI port opening wedges (`midiOutOpen ->
      //      MMSYSERR_ERROR`), curable ONLY by a daemon restart. Observed
      //      live on 2026-08-01; a separate process could open the same
      //      device id while OPBridge could not.
      //   2. The PEDAL stops applying Program Changes, because openhx never
      //      closes the session it opened. No host-side call fixes this
      //      today — the session-close packet has never been captured.
      //
      // (1) is ours to clean up and there is no reason to make the operator
      // do it, so we do it here. Deliberately AFTER the read, never before:
      // the escalation restarts the daemon, and restarting mid-probe would
      // throw away the 8 seconds of USB we just spent.
      //
      // Failure is swallowed from the OPERATOR's view on purpose. The
      // library read is the point; if the rescan cannot run, the worst case
      // is the pre-existing manual recovery, and surfacing a second error
      // over a successful read would read as "the probe failed" when it did
      // not. But a swallow with literally no trace anywhere is how "buttons
      // stopped firing after a Helix read" would show up with nothing to
      // grep for — so it is at least logged, console-only, not user-facing.
      try { await client.midiRescan(true); } catch (err) {
        console.warn('[helix] post-probe midiRescan failed - OPBridge\'s own MIDI port may still be wedged:', err);
      }

      if (probe.midi_program_changes_stalled) {
        // Only the PEDAL half is left, so say only that. The old copy told
        // the operator to run /midi/rescan by hand — work OPButtons has just
        // done for them.
        setStall(
          'Done - but the pedal is ignoring MIDI Program Changes. '
          + 'Replug its USB cable (it stays powered) or open and quit HX Edit. '
          + 'OPBridge normally closes the pedal\'s session after every read, so this means '
          + 'that close did not take, or this OPBridge is older than the fix. '
          + 'OPBridge\'s own MIDI has already been cleared automatically.',
        );
      }
      apply(await client.helixPresets());
    } catch (err) {
      applyProbeError(err);
    }
  })().finally(() => { probeInFlight = null; });
  return probeInFlight;
}

/**
 * Auto-load the CACHED library when a bridge connects.
 *
 * Safe precisely because it is the GET and not the probe — it costs one
 * cheap HTTP call and no USB. This is what makes names appear on their own
 * after the operator has probed once in a previous session.
 */
export function installHelixAutoLoad(
  isConnected: () => boolean,
  hasHelixItem: () => boolean,
): void {
  createEffect(() => {
    if (!isConnected() || !hasHelixItem()) return;
    refreshHelixDeviceStatus();
    // Every (re)connect, not once: names read on OPBridge's own screen or
    // another device since reach this one's tiles (2026-10-10).
    const s = untrack(statusSig);
    if (s === 'loading' || s === 'probing') return;
    void loadHelixLibrary();
  });
}

/** Test seam: reset module state between smokes. */
export function __resetHelixLibrary(): void {
  setLibrary(EMPTY);
  setStatus('idle');
  setError('');
  setStall('');
  setDevicePresent(null);
  loadInFlight = null;
  probeInFlight = null;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
